import type { TaskPermission, WorkflowTasksPermissionsResponse } from 'garaje/types/workflow-approvals';

// Keep only the task with the highest runNumber per nodeInstanceId.
// Using a plain object instead of Map to avoid ember/use-ember-get-and-set lint false positives.
function latestRunByNode(tasks: TaskPermission[]): TaskPermission[] {
  const latestByNode: Record<string, TaskPermission> = {};
  for (const task of tasks) {
    const existing = latestByNode[task.nodeInstanceId];
    if (!existing || task.runNumber > existing.runNumber) {
      latestByNode[task.nodeInstanceId] = task;
    }
  }
  return Object.values(latestByNode);
}

function isPendingApproverFor(task: TaskPermission, requestUserId: string): boolean {
  return (
    task.nodeType === 'approval' &&
    task.statusId === 'pending' &&
    task.participants.some((p) => p.roleType === 'APPROVER' && p.participantId === requestUserId)
  );
}

/**
 * Derives whether the current user can approve a workflow by inspecting the
 * tasks/permissions response. Keeps only the latest run per nodeInstanceId
 * (highest runNumber), then checks if the task is an approval node, is pending,
 * and requestUserId matches a participant with roleType 'APPROVER'. Short-circuits
 * to false if the backend explicitly reports requestUserCanReview === false.
 */
export function deriveCanApproveWorkflow(response: WorkflowTasksPermissionsResponse): boolean {
  if (response.requestUserCanReview === false) return false;

  const { requestUserId, tasks } = response;
  if (!tasks?.length) return false;

  return latestRunByNode(tasks).some((task) => isPendingApproverFor(task, requestUserId));
}

/**
 * True when the user is listed as an APPROVER on a pending task in the latest run,
 * but the backend reports that they no longer hold the required review permission.
 */
export function deriveUserIsAssignedApproverWithoutPermission(response: WorkflowTasksPermissionsResponse): boolean {
  if (response.requestUserCanReview !== false) return false;

  const { requestUserId, tasks } = response;
  if (!tasks?.length) return false;

  return latestRunByNode(tasks).some((task) => isPendingApproverFor(task, requestUserId));
}
